export interface IntentTrainingData {
  intent: string;
  utterances: string[];
}

/**
 * Training data for the NLU intent classifier (Layer 2).
 *
 * IMPORTANT: Layer 1 (keyword matching) already catches tokens like
 * "assenza", "assente", "malato", "malattia", "pasta", "bianco", "bianca",
 * "non presente", "pasta in bianco", "giustifica", "mancanza".
 * Child names and dates are also consumed before NLU runs.
 *
 * The NLU only receives the "noise" tokens — text remaining after keyword,
 * child, and date extraction. Therefore:
 * - Do NOT include phrases that contain Layer 1 keywords (they won't reach NLU)
 * - Focus on periphrastic, colloquial, and telegraphic expressions
 * - Include realistic "non mangia" contrasts for disambiguation
 */
export const TRAINING_DATA: IntentTrainingData[] = [
  {
    intent: 'assenza',
    utterances: [
      // --- Periphrasis: not coming / staying home ---
      'non viene a scuola',
      'non viene a mensa',
      'resta a casa',
      'rimane a casa',
      'sta a casa',
      'lo tengo a casa',
      'la tengo a casa',
      'non può venire',
      'non può andare a scuola',
      'non ci sarà',
      'non sarà presente',
      'non si presenta',
      'non lo mando a scuola',
      'non la mando a scuola',
      'non riesce a venire',
      'niente scuola',
      // --- Telegraphic / short ---
      'a casa',
      'febbre',
      'sta male',
      'non sta bene',
      'è indisposto',
      'è indisposta',
      'ha vomitato',
      'ha la tosse',
      'ha il raffreddore',
      'ha la febbre',
      'ha influenza',
      'ha la gastroenterite',
      'ha mal di gola',
      'ha otite',
      // --- Medical / appointment ---
      'ha un appuntamento medico',
      'ha una visita',
      'ha pediatra',
      'deve fare una visita',
      'permesso medico',
      'esce prima quindi non mangia',
      // --- Skipping lunch / canteen ---
      'salta il pranzo',
      'non si ferma a pranzo',
      'non pranza',
      'salta mensa',
      'non resta a mensa',
      'niente pranzo',
      'niente mensa',
      // --- "non mangia" + absent cause (contrast with pasta-in-bianco) ---
      'non mangia perché è a casa',
      'non mangia perché non viene',
      'non mangia a mensa perché è a casa',
      'non pranza perché resta a casa',
      'non mangia a scuola',
      'non mangia è a casa',
      // --- Typos ---
      'non viee a scuola',
      'non puo venire',
    ]
  },
  {
    intent: 'pasta-in-bianco',
    utterances: [
      // --- Direct requests ---
      'pasto leggero',
      'mangia semplice',
      'dieta leggera',
      'niente condimento',
      'mangia leggero',
      'senza sugo',
      'senza condimento',
      'scondito',
      'solo primo',
      'solo riso',
      'cibo leggero',
      'pasto leggero per favore',
      // --- Colloquial / telegraphic ---
      'mangia in bianco',
      'solo in bianco',
      'fategli mangiare in bianco',
      'fategli fare il pasto semplice',
      'in bianco per favore',
      'in bianco',
      'dieta in bianco',
      'stomaco delicato',
      // --- Stomach / health related ---
      'ha mal di pancia mangia leggero',
      'ha mal di stomaco solo primo',
      'stomaco delicato mangia semplice',
      // --- "non mangia" + meal restriction (contrast with assenza) ---
      'non mangia il pasto normale',
      'non mangia il menu normale',
      'non mangia il secondo solo primo',
      'non mangia con il sugo',
      'non mangia il pranzo normale solo semplice',
      'non mangia pesante',
      'mangia solo cose leggere',
      // --- Typos ---
      'in biaco',
      'pasto legero',
    ]
  },
  {
    intent: 'rimuovi-assenza',
    utterances: [
      // --- Undo / cancel ---
      'non serve più',
      'ho sbagliato a segnare',
      'era un errore',
      'torna a scuola',
      'viene a scuola',
      'sta meglio viene',
      'sta meglio torna',
      'è guarito',
      'è guarita',
      'non è più malato',
      'non è più malata',
      'alla fine viene',
      'ha recuperato',
      'non è più a casa',
      'si è ripreso',
      'si è ripresa',
      'rientra a scuola',
    ]
  },
  {
    intent: 'rimuovi-pasta-in-bianco',
    utterances: [
      // --- Undo / cancel ---
      'mangia normale',
      'mangia il pasto normale',
      'può mangiare tutto',
      'mangia il menu completo',
      'non serve più il pasto leggero',
      'sta meglio mangia normale',
      'niente più dieta',
      'torna al menu normale',
      'può mangiare di tutto',
      'mangia come gli altri',
      'mangia regolare',
      'non ha più mal di pancia',
      'non ha più mal di stomaco',
      'stomaco a posto',
    ]
  },
  {
    intent: 'genera-pagamento',
    utterances: [
      // --- Direct requests ---
      'voglio fare una ricarica',
      'devo ricaricare',
      'genera un bollettino',
      'fai un pagamento',
      'devo pagare la mensa',
      'voglio pagare',
      'voglio ricaricare',
      'crea un bollettino',
      'fai una ricarica',
      'prepara un bollettino',
      'genera il pagamento',
      // --- Obligation / need ---
      'devo fare un pagamento',
      'serve una ricarica',
      'bisogna pagare la mensa',
      'devo fare il bollettino',
      'è ora di pagare',
      'devo ricaricare il conto',
      'mi serve il bollettino',
      // --- Colloquial / telegraphic ---
      'metti soldi',
      'ricarica il conto mensa',
      'aggiungi credito',
      'carica il conto',
      'metti credito',
      'fai il bollettino',
      // --- Amount mentioned (keywords stripped, amount extracted before NLU) ---
      'devo mettere dei soldi',
      'vorrei fare una ricarica per la mensa',
      'fai il pagamento mensa',
      'paga la mensa',
    ]
  },
  {
    intent: 'None',
    utterances: [
      // --- Greetings / pleasantries ---
      'ciao',
      'salve',
      'buongiorno',
      'buonasera',
      'grazie',
      'grazie mille',
      'ok',
      'va bene',
      'perfetto',
      // --- Questions / help ---
      'come funziona',
      'aiuto',
      'aiutami',
      'come faccio',
      'non capisco',
      'chi sei',
      'cosa puoi fare',
      'che posso scrivere',
      'come si usa',
      'come segnalo',
      // --- Menu / info requests (not an action) ---
      'che ore sono',
      'menu di oggi',
      'che c\'è da mangiare',
      'qual è il menu',
      'dove vedo il menu',
      'quanto costa',
      'serve certificato',
      // --- Commands / navigation ---
      'annulla',
      'esci',
      'chiudi',
      'annulla tutto',
      'non funziona',
      // --- Hard negatives (look like intent but aren't) ---
      'non mangia le verdure',
      'mio figlio mangia poco',
      'ha fame',
      'mangia troppo',
      'non gli piace il pranzo',
      'posso segnare due figli',
      'voglio modificare una prenotazione',
      // --- Hard negatives: allergies/intolerances (must NOT trigger pasta-in-bianco or genera-pagamento) ---
      'mio figlio è celiaco',
      'ha un\'allergia',
      'intolleranza al lattosio',
      'è allergico al glutine',
      'ha intolleranze alimentari',
      // --- Hard negatives: info/cost requests (must NOT trigger genera-pagamento) ---
      'quanto costa la mensa',
      'quanto devo pagare al mese',
      'a che ora mangiano',
      'a che ora è il pranzo',
      'orario mensa',
      'dove vedo quanto ho speso',
      'quanto credito ho',
      'voglio vedere il saldo',
      // --- Hard negatives: admin/other ---
      'cambio scuola',
      'iscrizione mensa',
    ]
  }
];
